import {
  Component,
  OnInit,
  ChangeDetectionStrategy,
  signal,
  HostListener,
  ElementRef,
} from '@angular/core';
import { combineLatest, filter, Observable, startWith } from 'rxjs';
import { OAuthenticationService } from 'src/app/shared/authentication/oauthentication.service';
import { Menu, Module } from '../../shared/store/navigation/types';
import { NavigationService } from '../../shared/store/navigation/navigation.service';
import { ModalContentComponent } from '../../shared/components/modal-content/modal-content.component';
import { ActionButton } from '../../shared/components/modal-content/action-button';
import { FormService } from '../../shared/store/form/form.service';
import { NavigationEnd, Router } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ModalService } from '../../shared/service/modal.service';
import { NavigationApiService } from '../../shared/store/navigation/navigation.api.services';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import {
  commonSegmentCount,
  routeSegments,
} from '../../shared/helper/routehelper';
import { ModalComponentKey } from 'src/app/shared/service/modal-component';

type MenuMatch = {
  moduleId: string;
  groupKey?: string;
  menuKey: string;
  /** Nombre de segments de tête communs entre la route du menu et l'URL. */
  score: number;
  /** La route du menu est intégralement préfixe de l'URL. */
  full: boolean;
};

@UntilDestroy()
@Component({
  selector: 'app-sidebar',
  templateUrl: './sidebar.component.html',
  styleUrls: ['./sidebar.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class SidebarComponent implements OnInit {
  modules$: Observable<Module[]>;

  private static readonly STORAGE_KEY = 'sidebar.collapsed';
  readonly isSidebarCollapsed = signal(false);
  readonly isParentMenuCollapsed = signal(new Map<string, boolean>());

  // Module/groupe/item contenant la route active. Sert à mettre en surbrillance
  // le niveau visible (module quand le rail est replié, groupe quand son
  // sous-menu est replié, sinon l'item lui-même), et à ré-ouvrir le bon
  // sous-menu par défaut au dépliage.
  readonly activeModuleId = signal<string | null>(null);
  protected readonly activeGroupKey = signal<string | null>(null);
  protected readonly activeMenuKey = signal<string | null>(null);

  constructor(
    private readonly navigationService: NavigationService,
    private readonly navigationApiService: NavigationApiService,
    private readonly authService: OAuthenticationService,
    private readonly formService: FormService,
    private readonly router: Router,
    private readonly modalService: NgbModal,
    private readonly modalExchangeService: ModalService,
    private readonly elementRef: ElementRef<HTMLElement>,
  ) {
    this.modules$ = this.navigationService.getModules.value$;
  }

  /**
   * Replie le rail dès qu'on clique en dehors du menu latéral (comportement tiroir).
   * On ne persiste pas ce repli : le chevron reste le choix « épinglé » mémorisé,
   * le clic extérieur est un simple « je referme le tiroir ».
   */
  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (this.isSidebarCollapsed()) return;
    const target = event.target as Node | null;
    if (target && this.elementRef.nativeElement.contains(target)) return;
    this.isSidebarCollapsed.set(true);
    this.isParentMenuCollapsed.set(new Map<string, boolean>());
  }

  ngOnInit(): void {
    const storedCollapsed = localStorage.getItem(SidebarComponent.STORAGE_KEY);
    this.isSidebarCollapsed.set(
      storedCollapsed === null ? false : storedCollapsed === 'true',
    );

    this.authService.isAuthenticatedOrRefresh().then((result) => {
      if (result) {
        this.navigationService.getModules.call();
      }
    });

    // Synchronise l'arbre du menu avec l'URL courante : accès direct via URL
    // (route par défaut) ou navigation interne vers un lien d'un autre module.
    // On ouvre le module + le groupe sous-menu contenant la route active, sans
    // déplier le rail ni effacer les ouvertures manuelles de l'utilisateur.
    combineLatest([
      this.modules$,
      this.router.events.pipe(
        filter((e): e is NavigationEnd => e instanceof NavigationEnd),
        startWith(null),
      ),
    ])
      .pipe(untilDestroyed(this))
      .subscribe(([modules]) => this.syncActiveMenu(modules, this.router.url));
  }

  /**
   * Ouvre le module et l'éventuel groupe sous-menu dont la route correspond à `url`
   * (path-only, query ignorée), et mémorise l'item actif. En cas de matchs multiples,
   * on garde le plus spécifique pour cibler la page réellement affichée.
   */
  private syncActiveMenu(modules: Module[] | null, url: string): void {
    const target = routeSegments(url);
    if (target.length === 0 || !modules) return;

    const matches: MenuMatch[] = [];
    for (const module of modules) {
      for (const menu of module.menus ?? []) {
        this.collectMatches(menu, target, module.id, undefined, matches);
      }
    }
    if (matches.length === 0) {
      this.activeModuleId.set(null);
      this.activeGroupKey.set(null);
      this.activeMenuKey.set(null);
      return;
    }

    // Match le plus spécifique = plus de segments communs ; à égalité, un match
    // plein l'emporte sur un match tolérant au dernier segment.
    const best = matches.reduce((a, b) => (this.isBetterMatch(b, a) ? b : a));
    this.activeModuleId.set(best.moduleId);
    this.activeGroupKey.set(best.groupKey ?? null);
    this.activeMenuKey.set(best.menuKey);

    // Rail replié : sous-menus masqués, on ne remplit pas la map d'ouverture
    // (le module actif reste juste en surbrillance). Ils seront ré-ouverts au
    // dépliage via openActiveMenu().
    if (this.isSidebarCollapsed()) return;

    const keys = this.isParentMenuCollapsed();
    keys.set(best.moduleId, true);
    if (best.groupKey) keys.set(best.groupKey, true);
    this.isParentMenuCollapsed.set(new Map(keys));
  }

  /** Ouvre uniquement le module + groupe actifs (état « par défaut » au dépliage). */
  private openActiveMenu(): void {
    const map = new Map<string, boolean>();
    const moduleId = this.activeModuleId();
    const groupKey = this.activeGroupKey();
    if (moduleId) map.set(moduleId, true);
    if (groupKey) map.set(groupKey, true);
    this.isParentMenuCollapsed.set(map);
  }

  /** Ordre « le plus spécifique gagne » entre deux matchs. */
  private isBetterMatch(candidate: MenuMatch, current: MenuMatch): boolean {
    if (candidate.score !== current.score)
      return candidate.score > current.score;
    return candidate.full && !current.full;
  }

  private collectMatches(
    menu: Menu,
    target: string[],
    moduleId: string,
    groupKey: string | undefined,
    out: MenuMatch[],
  ): void {
    const route = menu.route ? routeSegments(menu.route) : [];
    if (route.length > 0) {
      const score = commonSegmentCount(route, target);
      const full = score === route.length;
      // Tolérance au dernier segment : le backend pointe parfois l'écran de liste
      // (`/Crm/SalesTeams/List`) alors que l'utilisateur est sur `/Details/:id` du
      // même écran. Le seuil de 2 segments évite qu'un simple module partagé
      // (`/Crm/Leads` vs `/Crm/LostReasons`) suffise à matcher.
      if (full || (score >= route.length - 1 && score >= 2)) {
        out.push({
          moduleId,
          groupKey,
          menuKey: this.menuKey(moduleId, menu.id),
          score,
          full,
        });
      }
    }
    for (const child of menu.children ?? []) {
      // Le groupe parent d'un enfant est identifié par `moduleId + menu.id`,
      // clé attendue par le template pour ouvrir le sous-groupe.
      this.collectMatches(
        child,
        target,
        moduleId,
        this.groupKey(moduleId, menu.id),
        out,
      );
    }
  }

  /** Identité d'un item de menu, préfixée par le module (les `id` sont par module). */
  private menuKey(moduleId: string, menuId: number): string {
    return moduleId + ':' + menuId.toString();
  }

  protected isMenuActive(moduleId: string, menuId: number): boolean {
    return this.activeMenuKey() === this.menuKey(moduleId, menuId);
  }

  /** Clé d'ouverture d'un groupe sous-menu, telle qu'utilisée par le template. */
  protected groupKey(moduleId: string, menuId: number): string {
    return moduleId + menuId.toString();
  }

  /**
   * Groupe replié contenant la route active : son item enfant actif est masqué,
   * on met le bouton du groupe en surbrillance à la place (même logique que le
   * bouton module quand le rail est replié).
   */
  protected isGroupActiveCollapsed(moduleId: string, menuId: number): boolean {
    const key = this.groupKey(moduleId, menuId);
    return (
      !this.isParentMenuCollapsed().get(key) && this.activeGroupKey() === key
    );
  }

  onSidebarToggle(): void {
    const collapsed = !this.isSidebarCollapsed();
    this.isSidebarCollapsed.set(collapsed);
    localStorage.setItem(SidebarComponent.STORAGE_KEY, String(collapsed));
    // Replié : tout fermé (sous-menus masqués, module actif en surbrillance).
    // Déplié : on ré-ouvre le sous-menu actif par défaut.
    if (collapsed) {
      this.isParentMenuCollapsed.set(new Map<string, boolean>());
    } else {
      this.openActiveMenu();
    }
  }

  logout() {
    this.formService.clear();
    this.authService.logout();
    this.router.navigateByUrl('/Login');
  }

  changePassword() {
    const modalRef = this.modalService.open(ModalContentComponent, {
      size: 'lg',
      backdrop: 'static',
    });
    modalRef.componentInstance.component =
      this.modalExchangeService.getComponentClass(
        ModalComponentKey.PasswordManage,
      );
    modalRef.componentInstance.title = 'ACCOUNT_LBL_TITLE';
    modalRef.componentInstance.cancelButton = true;
    modalRef.componentInstance.buttons = [
      <ActionButton>{
        actionName: 'changePassword',
      },
    ];

    // modalRef.result.then((id) => {}).finally(() => this.formService.clear());
  }

  protected onModuleClick(moduleId: string) {
    this.setSidebarCollapsed();
    this.setParentMenuCollapsed(moduleId);
  }

  protected onMenuClick(moduleId: string, menuId: number) {
    this.setParentMenuCollapsed(moduleId + menuId.toString());
  }

  private setSidebarCollapsed() {
    if (this.isSidebarCollapsed()) {
      this.isSidebarCollapsed.set(!this.isSidebarCollapsed());
      localStorage.setItem(
        SidebarComponent.STORAGE_KEY,
        String(this.isSidebarCollapsed()),
      );
    }
  }

  private setParentMenuCollapsed(key: string) {
    // Nouvelle Map (référence distincte) : l'émission du signal ne doit pas
    // dépendre du comportement par défaut d'Angular (objet toujours « changé »),
    // pour rester correct même si un `equal` custom est ajouté un jour.
    const next = new Map(this.isParentMenuCollapsed());
    next.set(key, !next.get(key));
    this.isParentMenuCollapsed.set(next);
  }
}
